import { type Moment } from "moment";
import { isRecord } from "../../../types/internal/utility";

export type SalePaymentStatus = "approved" | "cancelled" | "declined" | "completed" | "failed";
export type SaleProductType =
    "Normal" |
    "Product" |
    "GiftCard" |
    "Voucher" |
    "Basket" |
    "Package" |
    "Component" |
    "Integrated" |
    "Surcharge";
export type SaleStatus = "COMPLETED" | "CANCELLED" | "PARKED" | "INCOMPLETE";

export type SaleGiftCard = {
    code: string;
    amount: number;
    expiry: string;
    source: string;
};

export interface SaleActiveSurcharge {
    id: number;
    amount: number;
}

export function isActiveSurcharge(value: unknown): value is SaleActiveSurcharge {
    if(!isRecord(value)) {
        return false;
    }

    return "id" in value && typeof value.id === "number" &&
        "amount" in value && typeof value.amount === "number";
}

export interface SaleProduct {
    uuid: string;
    type: SaleProductType;
    name: string;
    caseQuantity: number;
    isCase: boolean;
    quantity: number;
    loyalty: {
        earn: number;
        redeem: number;
    };
    prices: {
        price: number; // Will always be the correct price
        unlockedPrice: number; // The price if the product was unlocked
        normal: number; // The normal everyday price
        base: number; // The single price
        addPrice: number; // The additional price to add to the product cost (for components)
        removePrice: number; // The additional price to remove from the product (for components)
        // The additional price that has been applied to the product (for packages)
        additionalPrice: [ base: number, total: number ];
        discountRate?: number; // The rate to be used for a discount
    };
    tax: {
        id: false | string;
        amount: number;
    };
    surcharge: null | SaleActiveSurcharge;
    products: Array<SaleProduct>;
    defaultProducts: Array<SaleProduct>;
    preventManualDiscount: boolean;
    special: boolean;
    edited: boolean;
    promotions: Record<string, {
        name: string;
        active: boolean;
        quantity: number;
    }>;
    rebates: Record<string, {
        amount: number;
        quantity: number;
    }>;
    giftCard: SaleGiftCard | null;
    note: string;
    userId: null | string;
    priceSet?: string;
    deleted?: boolean;
    initiallyDeleted?: boolean;
    familyId: string | null;
    categoryId: string | null;
    tags: Array<string>;
    canUnlock: boolean;
    discountReason?: string;
    requestPrice?: boolean;
    lockQuantity: boolean;
    metaData: Record<string, any>;
    barcodeQuantity?: number;
    lineId: string; // Unique ID for this line, only used internally to track state
    priceList: boolean;
}

export interface SaleVoucherProduct extends SaleProduct {
    index: number;
}

export type SaleCustomer = {
    uuid: string;
};

export interface SalePayment {
    method: string;
    type: string;
    status: SalePaymentStatus;
    amount: number;
    cashout: number;
    rounding: number | undefined;
    metadata: string;
    registerId: string;
    userId?: string;
    processTime?: Moment;
    receipt?: string;
    reverted: boolean;
    uploaded?: boolean;
    voucherRefund?: boolean;
    subtype?: string;
}

export interface SaleTotals {
    sale: number;
    remaining: number;
    paid: number;
    savings: number;
    discount: number;
    change: number;
    cashout: number;
    rounding: number;
    surcharge: number;
}

export type SaleActionType =
    // If changing, modify SaleActionEnum.php
    "PRODUCT_SCAN" |
    "PRODUCT_SEARCH_ADD" |
    "PRODUCT_KEY_ADD" |
    "QUANTITY_OVERRIDE" |
    "INCREMENT" |
    "DECREMENT" |
    "REMOVE_PRODUCT" |
    "CUSTOMER_SCAN" |
    "CUSTOMER_SEARCH_ADD" |
    "CUSTOMER_KEY_ADD" |
    "REMOVE_CUSTOMER" |
    "ADD_PAYMENT" |
    "REMOVE_PAYMENT";

export interface SaleAction {
    timestamp: string;
    action: SaleActionType;
    // for product / customer scan this is the barcode,
    // for product / customer search add this is the search string
    // for product / customer key add this is the key's rules
    // for quantity override this is the new quantity,
    // for increment / decrement it is the amount incremented
    // for remove product this is "keyboard" or "trash"
    // for remove customer this is blank
    // for add / remove payment this is the internal id of the payment
    value: string;
    affecting: string; // The ID of the item this is affecting, e.g. product, customer, payment method
    user: string; // The ID of the user
}

export type SaleSurcharge = {
    id: number;
    type: "PERCENTAGE" | "FIXED";
    amount: number;
    taxRate: string | null;
    name: string;
    appliesTo: null | Array<{
        id: string;
        type: "Category" | "Tag";
    }>;
};

// Causes type errors if it's an interface ¯\_(ツ)_/¯
// Looks like it's related to https://github.com/microsoft/TypeScript/issues/15300
export type SaleState = {
    internalId: string;
    id?: string;
    invoiceId?: string;
    status?: SaleStatus;
    products: Array<SaleProduct>;
    customer: false | SaleCustomer;
    payments: Array<SalePayment>;
    notes: {
        internal: string;
        sale: string;
    };
    orderReference: string;
    refundReason: string;
    productDetails: {
        id: Array<string>;
        index: Array<number>;
    };
    totals: SaleTotals;
    createdAt?: string;
    calculate: boolean;
    registerId: false | string;
    userId: false | string;
    linkedTo: false | string;
    priceSet: string;
    printReceipt?: boolean;
    addingToSale: null | number;
    actions: Array<SaleAction>;
    surchargeRules: Array<SaleSurcharge>;
    surcharge: null | SaleActiveSurcharge;
    metaData: Record<string, unknown>;
};
